/**
 * Registro das áreas da suíte (SPEC-v2 2.1, 2.4, 2.5, 9.2): base da rota, tela inicial por papel e
 * função dentro de cada área, e o carregador sob demanda de cada área (`lazy()`, um chunk por área).
 */
import type { Area, SessionInfo } from '@rota/contracts';
import { AREA_ORDER } from '@/components/suite/areaMeta';
import { areaEntry, lastRoute } from '@/lib/areaMemory';

export { AREA_ORDER };

/** Caminho de entrada da área para a pessoa, relativo à área (ex.: "/conversas"). */
export function areaHomeFor(area: Area, s: SessionInfo | null): string {
  const role = s?.viewer.role;
  const fns = s?.functions ?? [];
  if (area === 'automacoes') {
    if (role === 'support') return fns.includes('social') ? '/conversas' : '/';
    if (role === 'expert') return '/conversas?fila=expert';
    if (role === 'tech') return '/saude';
    if (role === 'creative') return '/mensagens';
    if (role === 'traffic') return '/instagram';
    return '/';
  }
  if (area === 'estudio') {
    // 5.1: expert → Hoje; editor → Quadro com "meus cartões"; social → Calendário; tráfego →
    // Remessas; copy → Ganchos; producer → Hoje. 2.5: creative abre no Quadro com "meus cartões".
    if (role === 'creative') return '/quadro?dono=eu';
    if (role === 'traffic') return '/remessas';
    if (role === 'support') return '/calendario';
    if (fns.includes('editor') && !fns.includes('copy')) return '/quadro?dono=eu';
    return '/';
  }
  return '/';
}

/** Endereço completo ao entrar numa área: a última rota lembrada (2.4) ou a inicial da pessoa. */
export function enterArea(area: Area, s: SessionInfo | null): string {
  if (lastRoute(area)) return areaEntry(area);
  const home = areaHomeFor(area, s);
  return `/${area}${home === '/' ? '' : home}`;
}

/** A área de um caminho (`/automacoes/conversas` → `automacoes`). */
export function areaOfPath(path: string): Area | null {
  const seg = path.split('/')[1] ?? '';
  return (AREA_ORDER as readonly string[]).includes(seg) ? (seg as Area) : null;
}

/** Rotas antigas da V1 (SPEC 3.1) que redirecionam para `/painel/<rota>` (V2 2.1). */
export const LEGACY_PAINEL_SEGMENTS = [
  'hoje',
  'decisoes',
  'trafego',
  'criativos',
  'funil',
  'vsl',
  'perfis',
  'vendas',
  'recuperacao',
  'financeiro',
  'rastreamento',
  'relatorios',
  'pauta',
  'ajustes',
] as const;

/**
 * Destino de uma rota antiga, preservando a query e o fragmento. `/ajustes/usuarios` vai para
 * `/equipe/pessoas` (Usuários saiu do Painel). `null` = não é rota antiga.
 */
export function legacyRedirect(pathname: string, search: string, hash = ''): string | null {
  const seg = pathname.split('/')[1] ?? '';
  if (!(LEGACY_PAINEL_SEGMENTS as readonly string[]).includes(seg)) return null;
  const q = search ? (search.startsWith('?') ? search : `?${search}`) : '';
  if (/^\/ajustes\/usuarios\/?$/.test(pathname)) return `/equipe/pessoas${q}${hash}`;
  return `/painel${pathname}${q}${hash}`;
}

/** `volta` só aceita caminho interno (nada de `//host`, `http:` ou `javascript:`). */
export function safeReturnPath(v: string | null | undefined): string | null {
  if (!v?.startsWith('/') || v.startsWith('//') || v.startsWith('/\\')) return null;
  if (/^\/(entrar|configurar|convite|esqueci|redefinir)(\/|$|\?)/.test(v)) return null;
  return v;
}
